<script setup>
import { ref, onMounted } from 'vue';
import { setMeta } from '@/utils/util';
import { ElMessage } from 'element-plus';
import beautify from 'js-beautify';
import { js as beautifyJS, css as beautifyCSS, html as beautifyHTML } from 'js-beautify';

const code = ref('');
const formattedCode = ref('');
const language = ref('javascript');
const error = ref('');
const loading = ref(false);

const languageOptions = [
  { label: 'JavaScript', value: 'javascript' },
  { label: 'HTML', value: 'html' },
  { label: 'CSS', value: 'css' },
  { label: 'JSON', value: 'json' },
];

onMounted(() => {
  document.title = "代码美化工具 - 在线代码格式化";
  setMeta("description", "免费在线代码美化工具，支持多种编程语言，包括JavaScript、HTML、CSS和JSON等。提供代码格式化、美化和规范化功能。")
  setMeta("keywords", "代码格式化,代码美化,代码规范化,在线格式化工具,JavaScript格式化,HTML格式化,CSS格式化")
});

const formatJavaScript = (input) => {
  try {
    // 使用 Function 构造器验证 JavaScript 语法
    new Function(input);
    return beautifyJS(input, {
      indent_size: 2,
      space_in_empty_paren: true,
      preserve_newlines: true,
      max_preserve_newlines: 2,
      brace_style: 'collapse',
      break_chained_methods: true,
      keep_array_indentation: true,
      space_before_conditional: true,
      unescape_strings: true,
      wrap_line_length: 80,
      end_with_newline: true,
      comma_first: false,
      operator_position: 'before-newline'
    });
  } catch (e) {
    throw new Error('JavaScript语法错误');
  }
};

const formatHTML = (input) => {
  try {
    return beautifyHTML(input, {
      indent_size: 2,
      wrap_line_length: 80,
      preserve_newlines: true,
      max_preserve_newlines: 2,
      unformatted: ['code', 'pre', 'em', 'strong', 'span'],
    });
  } catch (e) {
    throw new Error('HTML格式化失败');
  }
};

const formatCSS = (input) => {
  try {
    return beautifyCSS(input, {
      indent_size: 2,
      preserve_newlines: true,
      max_preserve_newlines: 2,
    });
  } catch (e) {
    throw new Error('CSS格式化失败');
  }
};

const formatJSON = (input) => {
  try {
    const parsed = JSON.parse(input);
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    throw new Error('无效的JSON格式');
  }
};

const formatCode = async () => {
  if (!code.value.trim()) {
    error.value = '请输入需要格式化的代码';
    return;
  }

  error.value = '';
  loading.value = true;

  try {
    switch (language.value) {
      case 'javascript':
        formattedCode.value = formatJavaScript(code.value);
        break;
      case 'html':
        formattedCode.value = formatHTML(code.value);
        break;
      case 'css':
        formattedCode.value = formatCSS(code.value);
        break;
      case 'json':
        formattedCode.value = formatJSON(code.value);
        break;
    }
  } catch (err) {
    console.error('格式化错误:', err);
    error.value = `格式化错误: ${err.message}`;
    formattedCode.value = '';
  } finally {
    loading.value = false;
  }
};

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(formattedCode.value);
    ElMessage.success('代码已复制到剪贴板！');
  } catch (err) {
    ElMessage.error('复制到剪贴板失败');
  }
};

const clearCode = () => {
  code.value = '';
  formattedCode.value = '';
  error.value = '';
};

const loadExample = () => {
  switch (language.value) {
    case 'javascript':
      code.value = `function example(){const x=1;const y=2;return x+y;}const obj={a:1,b:2,c:3};const arr=[1,2,3,4,5];`;
      break;
    case 'html':
      code.value = `<!DOCTYPE html><html><head><title>示例</title></head><body><div class="container"><h1>你好，世界</h1><p>这是一个示例。</p></div></body></html>`;
      break;
    case 'css':
      code.value = `.container{width:100%;max-width:1200px;margin:0 auto;padding:20px;}.button{display:inline-block;padding:10px 20px;border-radius:4px;background-color:#007bff;color:white;}`;
      break;
    case 'json':
      code.value = `{"name":"张三","age":30,"city":"北京","hobbies":["阅读","音乐","运动"],"address":{"street":"主街道123号","zip":"100000"}}`;
      break;
  }
  formatCode();
};
</script>

<template>
  <div class="tool-container">
    <h1 style="margin-bottom: 20px">代码美化工具</h1>
    
    <div class="controls">
      <el-select v-model="language" placeholder="选择编程语言" @change="clearCode">
        <el-option
          v-for="option in languageOptions"
          :key="option.value"
          :label="option.label"
          :value="option.value"
        />
      </el-select>
      
      <div class="buttons">
        <el-button type="primary" @click="formatCode" :loading="loading">格式化代码</el-button>
        <el-button @click="clearCode">清空</el-button>
        <el-button @click="loadExample">加载示例</el-button>
      </div>
    </div>

    <div class="code-area">
      <div class="input-section">
        <label>输入代码：</label>
        <el-input
          v-model="code"
          type="textarea"
          :rows="15"
          placeholder="在此粘贴您的代码..."
          :spellcheck="false"
        />
      </div>
      
      <div class="output-section">
        <div class="output-header">
          <label>格式化结果：</label>
          <el-button 
            v-if="formattedCode"
            type="primary" 
            link 
            @click="copyToClipboard"
          >
            复制到剪贴板
          </el-button>
        </div>
        <el-input
          v-model="formattedCode"
          type="textarea"
          :rows="15"
          readonly
          :spellcheck="false"
        />
      </div>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      style="margin-top: 20px;"
    />

    <div class="introduce">
      <h2>什么是代码美化工具？💻</h2>
      <p>
        代码美化工具是一个强大的在线工具，可以帮助您格式化和美化代码。
        它支持多种编程语言，并提供一致的格式化规则。
      </p>
      
      <h3>功能特点</h3>
      <ul>
        <li>支持多种编程语言</li>
        <li>统一的代码格式化规则</li>
        <li>实时格式化</li>
        <li>便捷的复制功能</li>
        <li>可自定义的格式化选项</li>
      </ul>

      <h3>支持的语言</h3>
      <ul>
        <li>JavaScript - 使用现代标准格式化JS代码</li>
        <li>HTML - 正确缩进和组织HTML标记</li>
        <li>CSS - 格式化样式表以提高可读性</li>
        <li>JSON - 格式化和验证JSON数据</li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tool-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.controls {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.buttons {
  display: flex;
  gap: 10px;
}

.code-area {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.input-section,
.output-section {
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-weight: bold;
    color: #606266;
  }
}

.output-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

:deep(.el-textarea__inner) {
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre;
  overflow-wrap: normal;
  overflow-x: auto;
}

.introduce {
  margin-top: 40px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  h2 {
    margin-bottom: 16px;
  }

  h3 {
    margin: 20px 0 10px;
    color: #2c3e50;
  }

  p {
    margin-bottom: 16px;
    line-height: 1.6;
    color: #606266;
  }

  ul {
    margin: 0;
    padding-left: 20px;
    
    li {
      margin-bottom: 8px;
      color: #606266;
    }
  }
}

@media (max-width: 768px) {
  .code-area {
    grid-template-columns: 1fr;
  }
}
</style> 